/**
 * MemberProfile Component - Comprehensive user profile management interface - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Provides a tabbed interface for managing user profiles with photo/video upload capabilities,
 * field visibility controls, and form validation. Features include profile image management,
 * video upload/playback, LinkedIn profile validation, and bio editing with HTML parsing.
 * 
 * @component
 * @returns {JSX.Element} The MemberProfile component with tabs for Profile, Bio, and Video
 * 
 * @example
 * <MemberProfile />
 */

import React, { useState, useEffect, useRef, useContext } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import Parser from 'html-react-parser';
import apiHelper from '../../utils/apiHelper';
import './MemberProfile.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthContext } from '../Auth/AuthContext';

function MemberProfile() {

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedProfile, setEditedProfile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [videoUploading, setVideoUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const fileInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const { user: [user] } = useContext(AuthContext);
    const [profileImage, setProfileImage] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const [showUploadMessage, setShowUploadMessage] = useState(false);
    const [linkedInError, setLinkedInError] = useState('');
    const [videoSource, setVideoSource] = useState(null);
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const currentBlobUrl = useRef(null);
    const loadingRef = useRef(false);
    const [fieldVisibility, setFieldVisibility] = useState({
        email: true,
        linkedIn: true,
        jobTitle: true
    });
    const [yearsError, setYearsError] = useState('');
    const [currentTabIndex, setCurrentTabIndex] = useState(0);

    const WORKING_GROUP_ROLES = [
        { value: '', label: 'Select a role' },
        { value: 'Member', label: 'Member' },
        { value: 'Chair', label: 'Chair' },
        { value: 'Vice Chair', label: 'Vice Chair' }
    ];

    const MEMBER_COHORTS = [
        { value: '', label: 'Select a cohort' },
        { value: 'Admin/Custodian', label: 'Admin/Custodian' },
        { value: 'Asset manager', label: 'Asset manager' },
        { value: 'Auditor', label: 'Auditor' },
        { value: 'Innovation, Tech & Infrastructure', label: 'Innovation, Tech & Infrastructure' },
        { value: 'Legal', label: 'Legal' },
        { value: 'Irish Funds Employee', label: 'Irish Funds Employee' },
        { value: 'Other', label: 'Other' }
    ];

    // Fetch profile data on component mount
    useEffect(() => {
        /**
         * Fetches profile data from API based on user email
         * Updates profile state and handles loading/error states
         */
        const fetchProfileData = async () => {
            try {
                setLoading(true);
                const profileData = await apiHelper.getMemberProfileByEmail(user.profile.email);
                
                setProfile(profileData);
                setEditedProfile(profileData);
                setLoading(false);
            } catch (error) {
                setError('Failed to load profile data');
                setLoading(false);
            }
        };

        fetchProfileData();
    }, [user?.profile?.email]);

    // Load profile image when profile photo changes
    useEffect(() => {
        /**
         * Loads profile image from server using authenticated API call
         * Creates blob URL for image display and handles cleanup
         */
        const loadProfileImage = async () => {
            if (!profile?.photo) return;  // Check for photo instead of email

            try {
                // Use the photo filename from profile data
                const imageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/profile-images/${profile.photo}`;
                const authToken = await apiHelper.getAuthToken();
                
                const response = await fetch(imageUrl, {
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    }
                });

                if (!response.ok) {
                    setProfileImage(null);
                    return;
                }

                const blob = await response.blob();
                const imageObjectUrl = URL.createObjectURL(blob);
                setProfileImage(imageObjectUrl);
            } catch (error) {
                setProfileImage(null);
            }
        };

        loadProfileImage();

        return () => {
            if (profileImage) {
                URL.revokeObjectURL(profileImage);
            }
        };
    }, [profile?.photo]); // Changed dependency to photo

    // Set edit permissions based on user email match
    useEffect(() => {
        if (user && profile) {
            const canEditProfile = user.profile.email === profile.email;
            setCanEdit(canEditProfile);
        }
    }, [user, profile]);

    // Update field visibility settings from profile data
    useEffect(() => {
        if (profile?.fieldVisibility) {
            setFieldVisibility(profile.fieldVisibility);
        }
    }, [profile]);

    // Load video content when video link changes
    useEffect(() => {
        /**
         * Loads video content from server with fallback to direct URL
         * Handles blob URL creation and cleanup for video playback
         */
        const loadVideo = async () => {
            if (loadingRef.current || !profile?.videoLink) return;

            loadingRef.current = true;
            setIsVideoLoading(true);

            try {
                const tempVideoUrl = `${profile.videoURL}`;

                if(tempVideoUrl){
                    setVideoSource(tempVideoUrl);
                    return;
                }

                const authToken = await apiHelper.getAuthToken();
                const videoLink = `profile-videos/${profile.videoLink}`;
                const videoUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${videoLink}`;
                const response = await fetch(videoUrl, {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                });

                if (!response.ok) throw new Error('Failed to fetch video');

                const blob = await response.blob();
                // Clean up previous blob URL if it exists
                if (currentBlobUrl.current) {
                    URL.revokeObjectURL(currentBlobUrl.current);
                }
                // Create new blob URL
                const newBlobUrl = URL.createObjectURL(blob);
                currentBlobUrl.current = newBlobUrl;
                setVideoSource(newBlobUrl);

            } catch (err) {
                console.error('Error loading video:', err);
                setVideoSource(null);
            } finally {
                setIsVideoLoading(false);
                loadingRef.current = false;
            }
        };

        loadVideo();

        // Cleanup
        return () => {
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current);
                currentBlobUrl.current = null;
            }
            loadingRef.current = false;
        };
    }, [profile?.videoLink]);

    /**
     * Handles profile image upload with validation and state management
     * Updates profile data and triggers image reload with success notifications
     * 
     * @param {Event} event - File input change event
     */
    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setImageUploading(true);
            setUploadError(null);

            await apiHelper.uploadMemberProfileImage(file, profile.id);
            
            // Get fresh profile data
            const freshProfileData = await apiHelper.getMemberProfileByEmail(user.profile.email);
            setProfile(freshProfileData);
            setEditedProfile(freshProfileData);

            setImageUploading(false);
            setShowUploadMessage(true);
            toast.success('Image uploaded successfully!');
            
         
            window.dispatchEvent(new CustomEvent('profileImageChanged'));
        } catch (err) {
            setUploadError('Failed to upload image. Please try again.');
            setImageUploading(false);
            toast.error('Failed to upload image.');
            console.error('Error uploading image:', err);
        }
    };

    /**
     * Handles video upload with progress tracking and blob URL management
     * Fetches updated profile data and creates new video blob URL for playback
     * 
     * @param {Event} event - File input change event
     */
    const handleVideoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setVideoUploading(true);
            setUploadError(null);
            
            // Upload the video
            const response = await apiHelper.uploadProfileVideo(file, profile.id);

            // Fetch fresh profile data
            let freshProfileData;
            freshProfileData = await apiHelper.getMemberProfileByEmail(user.profile.email);

            setProfile(freshProfileData);
            setEditedProfile(freshProfileData);

            // Load the new video
            const videoPath = `profile-videos/${response.fileName}`;
            const authToken = await apiHelper.getAuthToken();
            const videoUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${videoPath}`;

            const videoResponse = await fetch(videoUrl, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (!videoResponse.ok) throw new Error('Failed to fetch new video');

            const blob = await videoResponse.blob();

            // Clean up old blob URL
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current);
            }

            // Create new blob URL and update video source
            const newBlobUrl = URL.createObjectURL(blob);
            currentBlobUrl.current = newBlobUrl;
            setVideoSource(newBlobUrl);

            setVideoUploading(false);
            toast.success('Video uploaded successfully!');
        } catch (err) {
            setUploadError('Failed to upload video. Please try again.');
            setVideoUploading(false);
            toast.error('Failed to upload video.');
            console.error('Error uploading video:', err);
        }
    };

    /**
     * Validates LinkedIn profile URL format
     * Checks if URL follows proper LinkedIn profile pattern
     * 
     * @param {string} url - LinkedIn profile URL to validate
     * @returns {boolean} True if valid LinkedIn URL, false otherwise
     */
    const validateLinkedInProfile = (url) => {
        const linkedInPattern = /^https:\/\/(www\.)?linkedin\.com\/.*$/;
        return linkedInPattern.test(url);
    };

    /**
     * Validates years in industry field input
     * Checks for valid number, positive value, and reasonable range
     * 
     * @param {string|number} years - Years value to validate
     * @returns {string} Error message if invalid, empty string if valid
     */
    const validateYears = (years) => {
        if (years === '' || years === null || years === undefined) return ''; // Allow empty/null/undefined values
        const numYears = Number(years);
        if (isNaN(numYears)) return 'Please enter a valid number';
        if (numYears < 0) return 'Years cannot be negative';
        if (numYears > 100) return 'Please enter a realistic number of years';
        if (!Number.isInteger(numYears)) return 'Please enter a whole number';
        return ''; // validation passed
    };

    /**
     * Handles saving profile changes with comprehensive validation
     * Validates required fields, LinkedIn URL, and years in industry
     * Updates profile data and manages edit state
     */
    const handleSaveChanges = async () => {
        // Validate name (mandatory field)
        if (!editedProfile.name || !editedProfile.name.trim()) {
            toast.error('Name is required and cannot be empty');
            return;
        }
        
        // Validate LinkedIn URL
        if (editedProfile.linkedInProfile && !validateLinkedInProfile(editedProfile.linkedInProfile)) {
            setLinkedInError('Invalid LinkedIn profile URL');
            return;
        }
        
        // Add years validation
        const yearsError = validateYears(editedProfile.NoOfYrsintheIndustry);
        if (yearsError) {
            setYearsError(yearsError);
            toast.error('Please correct the years in industry field');
            return;
        }

        try {
            setSaving(true);
            const updatedProfile = await apiHelper.updateProfile({
                id: profile.id,
                name: editedProfile.name,
                linkedInProfile: editedProfile.linkedInProfile,
                jobTitle: editedProfile.jobTitle,
                NoOfYrsintheIndustry: editedProfile.NoOfYrsintheIndustry,
                fieldVisibility: fieldVisibility,
                bio: editedProfile.bio,
                workingGroupRole: editedProfile.workingGroupRole,
                company: editedProfile.company,
                memberCohort: editedProfile.memberCohort
            });

            setProfile(updatedProfile);
            setEditedProfile(updatedProfile);
            apiHelper.setUserProfile(updatedProfile);
            setIsEditing(false);
            setSaving(false);
            toast.success('Profile updated successfully!');
        } catch (err) {
            setError('Failed to save changes. Please try again.');
            setSaving(false);
            toast.error('Failed to update profile.');
            console.error('Error saving changes:', err);
        }
    };

    /**
     * Handles field visibility toggle for profile fields
     * Updates both field visibility state and edited profile data
     * 
     * @param {string} field - Field name to toggle visibility for
     */
    const handleVisibilityChange = (field) => {
        setFieldVisibility(prev => ({
            ...prev,
            [field]: !prev[field]
        }));

        // Update editedProfile to include visibility settings
        setEditedProfile(prev => ({
            ...prev,
            fieldVisibility: {
                ...prev.fieldVisibility,
                [field]: !fieldVisibility[field]
            }
        }));
    };

    /**
     * ProfileImage Component - Renders profile image with upload/delete capabilities
     * Handles image display, upload overlay, and delete functionality
     * 
     * @returns {JSX.Element} Profile image component with interactive controls
     */
    const ProfileImage = () => {

        /**
         * Handles image upload trigger by clicking file input
         */
        const handleImageClick = () => {
 
            if (fileInputRef.current) {
                fileInputRef.current.click();
            } 
        };

        return (
            <div style={{ position: 'relative' }}>
                {profileImage ? (
                    <div className="image-container" style={{ position: 'relative' }}>
                        <img
                            src={profileImage}
                            alt={profile?.name || 'Profile'}
                            style={{
                                width: "220px",
                                height: "220px",
                                objectFit: "cover",
                            }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                e.target.parentElement.classList.add('member-initials');
                                e.target.parentElement.textContent = profile?.name ? profile.name.split(' ').map(n => n[0]).join('') : '?';
                            }}
                        />
                        {canEdit && (
                            <>
                                <div
                                    className="upload-overlay"
                                    onClick={handleImageClick}
                                >
                                    <i className="fas fa-camera"></i>
                                    {imageUploading ? 'Uploading...' : 'Change Photo'}
                                </div>
                                <div
                                    className="delete-icon"
                                    onClick={async () => {
                                        if (window.confirm('Are you sure you want to remove this photo?')) {
                                            try {
                                                setImageUploading(true);
                                                setUploadError(null);

                                                await apiHelper.deleteProfileImage(profile.id);
                                                
                                                // Get fresh profile data after deletion
                                                const freshProfileData = await apiHelper.getMemberProfileByEmail(user.profile.email);
                                                setProfile(freshProfileData);
                                                setEditedProfile(freshProfileData);
                                                
                                                setProfileImage(null);
                                                setImageUploading(false);
                                                toast.success('Profile image deleted successfully');
                                                
                                                // Dispatch the event after we have fresh data
                                                window.dispatchEvent(new CustomEvent('profileImageChanged'));
                                            } catch (err) {
                                                setUploadError('Failed to delete image. Please try again.');
                                                setImageUploading(false);
                                                toast.error('Failed to delete image');
                                                console.error('Error deleting image:', err);
                                            }
                                        }
                                    }}
                                    style={{ display: 'none' }}
                                >
                                    <i className="fas fa-trash-alt"></i>
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <div
                        className="member-initials"
                        
                    >
                        {profile?.name ? profile.name.split(' ').map(n => n[0]).join('') : '?'}
                        {canEdit && (
                            <div
                                className="upload-overlay"
                                onClick={handleImageClick}
                            >
                                <i className="fas fa-camera"></i>
                                {imageUploading ? 'Uploading...' : 'Add Photo'}
                            </div>
                        )}
                    </div>
                )}

                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => {
                       
                        handleImageUpload(e);
                    }}
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    style={{ display: 'none' }}
                />
                {uploadError && (
                    <div className="upload-error">
                        {uploadError}
                    </div>
                )}
                <style>
                    {`
                        .image-container:hover .upload-overlay,
                        .member-initials:hover .upload-overlay,
                        .image-container:hover .delete-icon {
                            display: flex !important;
                        }
                    `}
                </style>
            </div>
        );
    };

    if (loading) {
        return (
            <div className='totalPage' id='profilePage'>
                <section className="wrapper">
                    <div className="container mt-6 pb-6">
                        <div className="profile-content">
                            <div className="settings-section">
                                <div className="profile-header">
                                    <div className="member-avatar-wrapper">
                                        <div className="member-avatar-container loading-pulse">
                                            <div className="member-initials" style={{ backgroundColor: '#e9ecef' }}>
                                                <i className="fas fa-spinner fa-spin"></i>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="profile-info-list" style={{ width: '100%' }}>
                                        {[1, 2, 3, 4, 5].map((item) => (
                                            <div key={item} className="profile-info-item">
                                                <div className="loading-pulse" style={{ 
                                                    height: '24px', 
                                                    width: '100%',
                                                    backgroundColor: '#e9ecef',
                                                    borderRadius: '4px'
                                                }}></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    if (error) {
        return (
            <div className='totalPage' id='profilePage'>
                <section className="wrapper">
                    <div className="container mt-6 pb-6">
                        <div className="error-message">Error: {error}</div>
                    </div>
                </section>
            </div>
        );
    }
    return (
        <>
            <ToastContainer />

            {profile ? (
                <>
                    <div className="profile-content">
                        <div className="settings-section">
                            <div className="profile-header">
                                <div className="member-avatar-wrapper">
                                    <div className={`member-avatar-container ${isEditing ? 'editable' : ''}`}>
                                        <ProfileImage />
                                        {showUploadMessage && (
                                            <div className="upload-info-badge">
                                                <i className="fas fa-check-circle text-success me-2"></i>
                                                Photo updated
                                            </div>

                                        )}
                                    </div>
                                </div>

                                <Tabs className='profile-tabs w-100' onSelect={index => setCurrentTabIndex(index)}>
                                    <TabList>
                                        <Tab>
                                            <i className="fa-solid fa-user"></i>
                                            <span className="tab-text">Profile</span>
                                        </Tab>
                                        <Tab>
                                            <i className="fa-solid fa-book"></i>
                                            <span className="tab-text">Bio</span>
                                        </Tab>
                                        <Tab style={{ display: 'none' }}>
                                            <i className="fa-solid fa-video"></i>
                                            <span className="tab-text">Video</span>
                                        </Tab>
                                    </TabList>

                                    <TabPanel>
                                        <div className="profile-info-list">
                                            <div className="profile-info-item">
                                                <span className="profile-info-label">Name {isEditing && <span style={{color: 'red'}}>*</span>}</span>
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={editedProfile.name || ''}
                                                        onChange={(e) => setEditedProfile(prev => ({
                                                            ...prev,
                                                            name: e.target.value
                                                        }))}
                                                        placeholder="Enter Your Name"
                                                        required
                                                    />
                                                ) : (
                                                    <span className="profile-info-value">{profile.name}</span>
                                                )}
                                            </div>
                                            <div className="profile-info-item">
                                                <span className="profile-info-label">Company</span>
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={editedProfile.company || ''}
                                                        onChange={(e) => setEditedProfile(prev => ({
                                                            ...prev,
                                                            company: e.target.value
                                                        }))}
                                                        placeholder="Enter Company Name"
                                                    />
                                                ) : (
                                                    <span className="profile-info-value">{profile.company || 'n/a'}</span>
                                                )}
                                            </div>
                                            <div className="profile-info-item">
                                                <div className="visibility-toggle-container">
                                                    <div className="profile-info-label-group">
                                                        <span className="profile-info-label">Job Title</span>
                                                    </div>
                                                    {isEditing && (
                                                        <div className="visibility-toggle">
                                                            <Tippy content={`${fieldVisibility.jobTitle ? 'Click to hide from profile' : 'Click to show on profile'}`}>
                                                                <div className="form-check form-switch">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id="jobTitleVisibility"
                                                                        checked={fieldVisibility.jobTitle}
                                                                        onChange={() => handleVisibilityChange('jobTitle')}
                                                                    />
                                                                </div>
                                                            </Tippy>
                                                        </div>
                                                    )}
                                                </div>
                                                {fieldVisibility.jobTitle ? (
                                                    isEditing ? (
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editedProfile.jobTitle || ''}
                                                            onChange={(e) => setEditedProfile(prev => ({
                                                                ...prev,
                                                                jobTitle: e.target.value
                                                            }))}
                                                            placeholder="Enter Job Title"
                                                        />
                                                    ) : (
                                                        <span className="profile-info-value">{profile.jobTitle || 'n/a'}</span>
                                                    )
                                                ) : (
                                                    <span className="profile-info-value text-muted">Hidden</span>
                                                )}
                                            </div>
                                            <div className="profile-info-item">
                                                <div className="visibility-toggle-container">
                                                    <div className="profile-info-label-group">
                                                        <span className="profile-info-label">Email</span>
                                                    </div>
                                                    {isEditing && (
                                                        <div className="visibility-toggle">
                                                            <Tippy content={`${fieldVisibility.email ? 'Click to hide from profile' : 'Click to show on profile'}`}>
                                                                <div className="form-check form-switch">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id="emailVisibility"
                                                                        checked={fieldVisibility.email}
                                                                        onChange={() => handleVisibilityChange('email')}
                                                                    />
                                                                </div>
                                                            </Tippy>
                                                        </div>
                                                    )}
                                                </div>
                                                {fieldVisibility.email ? (
                                                    profile.email ? (
                                                        <a href={`mailto:${profile.email}`} className="profile-info-value email-link">
                                                            <i className="fa-solid fa-envelope"></i> {profile.email}
                                                        </a>
                                                    ) : (
                                                        <span className="profile-info-value">n/a</span>
                                                    )
                                                ) : (
                                                    <span className="profile-info-value text-muted">Hidden</span>
                                                )}
                                            </div>
                                            <div className="profile-info-item">
                                                <span className="profile-info-label">Member Cohort</span>
                                                {isEditing ? (
                                                    <select
                                                        className="form-select"
                                                        value={editedProfile.memberCohort || ''}
                                                        onChange={(e) => setEditedProfile(prev => ({
                                                            ...prev,
                                                            memberCohort: e.target.value
                                                        }))}
                                                    >
                                                        {MEMBER_COHORTS.map(cohort => (
                                                            <option key={cohort.value} value={cohort.value}>
                                                                {cohort.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span className="profile-info-value">{profile.memberCohort || 'n/a'}</span>
                                                )}
                                            </div>
                                            <div className="profile-info-item">
                                                <div className="visibility-toggle-container">
                                                    <div className="profile-info-label-group">
                                                        <span className="profile-info-label">LinkedIn</span>
                                                    </div>
                                                    {isEditing && (
                                                        <div className="visibility-toggle">
                                                            <Tippy content={`${fieldVisibility.linkedIn ? 'Click to hide from profile' : 'Click to show on profile'}`}>
                                                                <div className="form-check form-switch">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id="linkedInVisibility"
                                                                        checked={fieldVisibility.linkedIn}
                                                                        onChange={() => handleVisibilityChange('linkedIn')}
                                                                    />
                                                                </div>
                                                            </Tippy>
                                                        </div>
                                                    )}
                                                </div>
                                                {fieldVisibility.linkedIn ? (
                                                    isEditing ? (
                                                        <div>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={editedProfile.linkedInProfile || ''}
                                                                onChange={(e) => setEditedProfile(prev => ({
                                                                    ...prev,
                                                                    linkedInProfile: e.target.value
                                                                }))}
                                                                placeholder="Enter LinkedIn URL"
                                                            />
                                                            {linkedInError && <div className="error-message" style={{ color: 'red', marginTop: '5px' }}>{linkedInError}</div>}
                                                        </div>
                                                    ) : (
                                                        profile.linkedInProfile ? (
                                                            <a href={profile.linkedInProfile}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="profile-info-value linkedin-link">
                                                                <i className="fab fa-linkedin"></i> View Profile
                                                            </a>
                                                        ) : (
                                                            <span className="profile-info-value">n/a</span>
                                                        )
                                                    )
                                                ) : (
                                                    <span className="profile-info-value text-muted">Hidden</span>
                                                )}
                                            </div>
                                            <div className="profile-info-item">
                                                <span className="profile-info-label">Number of years in Funds Industry</span>
                                                {isEditing ? (
                                                    <div>
                                                        <input
                                                            type="number"
                                                            className={`form-control ${yearsError ? 'is-invalid' : ''}`}
                                                            value={editedProfile.NoOfYrsintheIndustry || ''}
                                                            min="0"
                                                            max="100"
                                                            step="1"
                                                            onChange={(e) => {
                                                                const value = e.target.value === '' ? null : e.target.value;
                                                                const error = validateYears(value);
                                                                setYearsError(error);
                                                                setEditedProfile(prev => ({
                                                                    ...prev,
                                                                    NoOfYrsintheIndustry: value
                                                                }));
                                                            }}
                                                            placeholder="Enter years in industry"
                                                        />
                                                        {yearsError && (
                                                            <div className="invalid-feedback">
                                                                {yearsError}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="profile-info-value">
                                                        {profile.NoOfYrsintheIndustry || 'n/a'}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="profile-info-item">
                                                <div className="visibility-toggle-container">
                                                    <div className="profile-info-label-group">
                                                        <span className="profile-info-label">Working Group Role</span>
                                                    </div>
                                                </div>
                                                {isEditing ? (
                                                    <select
                                                        value={editedProfile.workingGroupRole || ''}
                                                        onChange={(e) => setEditedProfile(prev => ({
                                                            ...prev,
                                                            workingGroupRole: e.target.value
                                                        }))}
                                                        className="form-select"
                                                    >
                                                        {WORKING_GROUP_ROLES.map(role => (
                                                            <option key={role.value} value={role.value}>
                                                                {role.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span className="profile-info-value">
                                                        {profile.workingGroupRole || 'n/a'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </TabPanel>

                                    <TabPanel>
                                        {isEditing ? (
                                            <div className="bio-editor">
                                                <textarea
                                                    className="form-control"
                                                    value={editedProfile.bio || ''}
                                                    onChange={(e) => setEditedProfile(prev => ({
                                                        ...prev,
                                                        bio: e.target.value
                                                    }))}
                                                    rows={6}
                                                    placeholder="Write your bio here..."
                                                />
                                            </div>
                                        ) : (
                                            <div>
                                                {profile.bio
                                                    ? Parser(profile.bio.replace(/\n/g, '<br/>'))
                                                    : <p>No biography available.</p>
                                                }
                                            </div>
                                        )}
                                    </TabPanel>

                                    <TabPanel style={{ display: 'none' }}>
                                        <div className="video-section">
                                            {profile?.videoLink ? (
                                                <div className="video-player-container">
                                                    {isVideoLoading ? (
                                                        <div className="loading-spinner">
                                                            <i className="fas fa-spinner fa-spin"></i>
                                                            <span>Loading video...</span>
                                                        </div>
                                                    ) : videoSource ? (
                                                        <>
                                                            <video
                                                                controls
                                                                src={videoSource}
                                                            >
                                                                Your browser does not support the video tag.
                                                            </video>
                                                            {canEdit && (
                                                                <div className="video-controls-overlay">
                                                                    <button
                                                                        className="overlay-btn change-video"
                                                                        onClick={() => videoInputRef.current.click()}
                                                                    >
                                                                        <i className="fas fa-video"></i>
                                                                        Change Video
                                                                    </button>
                                                                    <button
                                                                        className="overlay-btn remove-video"
                                                                        onClick={async () => {
                                                                            if (window.confirm('Are you sure you want to remove this video?')) {
                                                                                try {
                                                                                    await apiHelper.deleteProfileVideo(profile.id);
                                                                                    setVideoSource(null);
                                                                                    let updatedProfile;
                                                                                    updatedProfile = await apiHelper.getMemberProfileByEmail(user.profile.email);

                                                                                    setProfile(updatedProfile);
                                                                                    setEditedProfile(updatedProfile);
                                                                                    toast.success('Video removed successfully');
                                                                                } catch (error) {
                                                                                    toast.error('Failed to remove video');
                                                                                }
                                                                            }
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-trash-alt"></i>
                                                                        Remove Video
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="video-error">
                                                            <i className="fas fa-exclamation-circle"></i>
                                                            Failed to load video
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="no-video-message">
                                                    {canEdit ? (
                                                        <div className="upload-prompt">
                                                            <i className="fas fa-video fa-2x text-muted mb-2"></i>
                                                            <p className="text-muted mb-3">No video uploaded yet</p>
                                                            <button
                                                                className="upload-btn"
                                                                onClick={() => videoInputRef.current.click()}
                                                            >
                                                                <i className="fas fa-cloud-upload-alt"></i>
                                                                Upload Video
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="text-muted">
                                                            <i className="fas fa-video fa-2x mb-2"></i>
                                                            <p>No video available</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <input
                                                type="file"
                                                ref={videoInputRef}
                                                onChange={handleVideoUpload}
                                                accept=".mp4,.mov,.avi,video/mp4,video/quicktime,video/x-msvideo"
                                                style={{ display: 'none' }}
                                            />

                                            {videoUploading && (
                                                <div className="upload-status">
                                                    <i className="fas fa-spinner fa-spin"></i>
                                                    Uploading video...
                                                </div>
                                            )}
                                            {uploadError && (
                                                <div className="upload-error">
                                                    <i className="fas fa-exclamation-circle"></i>
                                                    {uploadError}
                                                </div>
                                            )}
                                        </div>
                                    </TabPanel>
                                </Tabs>
                            </div>
                            {canEdit && currentTabIndex !== 2 && (
                                <div className="profile-edit-controls mt-4 text-end">
                                    {!isEditing ? (
                                        <i
                                            className="fas fa-edit edit-icon"
                                            onClick={() => setIsEditing(true)}
                                            style={{ cursor: 'pointer', fontSize: '1.5rem' }}
                                        ></i>
                                    ) : (
                                        <div className="edit-actions">
                                            <button
                                                className="btn btn-secondary me-2"
                                                onClick={() => {
                                                    setIsEditing(false);
                                                    setEditedProfile(profile);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleSaveChanges}
                                                disabled={saving}
                                            >
                                                {saving ? 'Saving...' : 'Save Changes'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <div className="no-profile-data">
                    <p>No member profile data available. Please check back later.</p>
                </div>
            )}
        </>
    );
}

export default MemberProfile;